/**
 * Decoding, peak extraction and WAV encoding for the voice-sample clipper.
 *
 * Deliberately not a waveform library. The page needs peaks for one canvas and a WAV
 * encoder for one upload; wavesurfer would be a dependency and a render loop for that.
 */

/** Cartesia accepts wav; 24 kHz mono is what the TTS models run at anyway. */
const TARGET_SAMPLE_RATE = 24_000;

export async function decodeAudio(file: Blob): Promise<AudioBuffer> {
  const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
  try {
    return await ctx.decodeAudioData(await file.arrayBuffer());
  } finally {
    // Safari caps concurrent contexts; a page that clips several files would otherwise
    // stop decoding partway through with no error the user can act on.
    void ctx.close();
  }
}

/** `buckets` peak amplitudes in 0..1, for drawing the waveform. */
export function extractPeaks(buffer: AudioBuffer, buckets: number): number[] {
  const samples = buffer.getChannelData(0);
  const perBucket = Math.max(1, Math.floor(samples.length / buckets));
  const peaks: number[] = [];
  for (let i = 0; i < buckets; i += 1) {
    const start = i * perBucket;
    let peak = 0;
    for (let j = start; j < start + perBucket && j < samples.length; j += 1) {
      const value = Math.abs(samples[j]);
      if (value > peak) peak = value;
    }
    peaks.push(peak);
  }
  return peaks;
}

/**
 * Mix `buffer` down to one channel, resample to 24 kHz and encode 16-bit PCM WAV.
 *
 * Linear resampling rather than an OfflineAudioContext: the input is a voice sample
 * already band-limited by the microphone, and the difference is inaudible at this length
 * while the code stays something you can read in one sitting.
 */
export function encodeClipToWav(
  buffer: AudioBuffer,
  startSeconds: number,
  durationSeconds: number,
): Blob {
  const start = Math.max(0, Math.min(startSeconds, buffer.duration));
  const end = Math.min(buffer.duration, start + durationSeconds);
  const ratio = TARGET_SAMPLE_RATE / buffer.sampleRate;
  const sourceStart = Math.floor(start * buffer.sampleRate);
  const sourceCount = Math.floor((end - start) * buffer.sampleRate);
  const outCount = Math.max(1, Math.floor(sourceCount * ratio));

  const channels = Array.from({ length: buffer.numberOfChannels }, (_, c) =>
    buffer.getChannelData(c),
  );

  const bytes = new ArrayBuffer(44 + outCount * 2);
  const view = new DataView(bytes);
  writeAscii(view, 0, "RIFF");
  view.setUint32(4, 36 + outCount * 2, true);
  writeAscii(view, 8, "WAVE");
  writeAscii(view, 12, "fmt ");
  view.setUint32(16, 16, true); // PCM header size
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, TARGET_SAMPLE_RATE, true);
  view.setUint32(28, TARGET_SAMPLE_RATE * 2, true); // byte rate
  view.setUint16(32, 2, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  writeAscii(view, 36, "data");
  view.setUint32(40, outCount * 2, true);

  for (let i = 0; i < outCount; i += 1) {
    const sourceIndex = sourceStart + Math.floor(i / ratio);
    let sample = 0;
    for (const channel of channels) sample += channel[sourceIndex] ?? 0;
    sample /= channels.length;
    const clamped = Math.max(-1, Math.min(1, sample));
    view.setInt16(44 + i * 2, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true);
  }

  return new Blob([bytes], { type: "audio/wav" });
}

function writeAscii(view: DataView, offset: number, text: string): void {
  for (let i = 0; i < text.length; i += 1) view.setUint8(offset + i, text.charCodeAt(i));
}

export function formatSeconds(value: number): string {
  const total = Math.max(0, Math.round(value));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}
